import { createContext, useState, useEffect, useRef } from "react";
import { number } from "../../../config";

export const RecurringTaskContext = createContext(null);

/**
 * wrapper component for recurring task context provider
 * @author Muskan Thakur
 */
export const RecurringTaskContextProvider = ({ children, initialValues = {}, recurrenceDefaults = {}, onRecurrenceEnabledChange = null }) => {
  const mapTriggerToContext = (trigger) => {
    if (!trigger) return {};
    const mapped = {
      startDate: trigger.StartDate,
      FrequencyTypeDate: trigger.FrequencyTypeDate,
      FrequencyTypeDuration: trigger.FrequencyTypeDuration,
      endCriteria: trigger.EndCriteria,
      recurrencePattern: trigger.TriggerPattern,
      recur: trigger.TriggerPattern === number.ONE ? trigger.FrequencyTypeDate : trigger.FrequencyTypeDuration,
      skipCountValue: trigger.FrequencyDay,
      duration: trigger.EndCriteria,
      endBydateValue: trigger.EndDate,
      endByCountValue: trigger.RepeatCount,
      recurrenceEnabled: trigger.IsRecurrenceDisabled === 0,
    };
    return mapped;
  };

  const mapValuesToObjects = (mappedValues) => {
    if (!recurrenceDefaults || Object.keys(recurrenceDefaults).length === 0) {
      return mappedValues;
    }

    const mapped = { ...mappedValues };

    if (mappedValues.skipCountValue !== undefined) {
      mapped.frequencyDay = mappedValues.skipCountValue;
    }

    if (mapped.FrequencyTypeDate && typeof mapped.FrequencyTypeDate === "number") {
      const frequencyObj = recurrenceDefaults.frequency?.find((item) => item.value === mapped.FrequencyTypeDate);
      if (frequencyObj) {
        mapped.FrequencyTypeDate = frequencyObj;
      }
    }

    if (mapped.endCriteria && typeof mapped.endCriteria === "number") {
      const endCriteriaObj = recurrenceDefaults.endCriteriaType?.find((item) => item.value === mapped.endCriteria);
      if (endCriteriaObj) {
        mapped.endCriteria = endCriteriaObj;
      }
    }

    if (mapped.FrequencyTypeDuration && typeof mapped.FrequencyTypeDuration === "number") {
      const frequencyIntervalObj = recurrenceDefaults.customRecurrenceTypes?.find((item) => item.value === mapped.FrequencyTypeDuration);
      if (frequencyIntervalObj) {
        mapped.FrequencyTypeDuration = frequencyIntervalObj;
      }
    }

    if (mapped.recur && typeof mapped.recur === "number") {
      const frequencyObj = recurrenceDefaults.frequency?.find((item) => item.value === mapped.recur);
      if (frequencyObj) {
        mapped.recur = frequencyObj;
      }
    }

    if (mapped.duration && typeof mapped.duration === "number") {
      const endCriteriaObj = recurrenceDefaults.endCriteriaType?.find((item) => item.value === mapped.duration);
      if (endCriteriaObj) {
        mapped.duration = endCriteriaObj;
      }
    }

    if (mapped.duration && typeof mapped.duration === "object" && mapped.duration.value) {
    } else if (mapped.duration && typeof mapped.duration === "number") {
      const endCriteriaObj = recurrenceDefaults.endCriteriaType?.find((item) => item.value === mapped.duration);
      if (endCriteriaObj) {
        mapped.duration = endCriteriaObj;
      }
    }

    return mapped;
  };

  const mappedValues = mapTriggerToContext(initialValues);

  const finalMappedValues = mapValuesToObjects(mappedValues);

  const [calenderValue, setCalenderValue] = useState(finalMappedValues.startDate ? new Date(finalMappedValues.startDate) : new Date());
  const [recurrencePattern, setRecurrencePattern] = useState(finalMappedValues.recurrencePattern || number.ONE);
  const [recur, setRecur] = useState(finalMappedValues.recur || {});
  const [reminder, setReminder] = useState(initialValues.reminder || []);
  const [title, setTitle] = useState(initialValues.title || null);
  const [duration, setDuration] = useState(() => {
    const durationValue = finalMappedValues.duration || finalMappedValues.endCriteria || {};
    return durationValue;
  });
  const [showEndByDatepicker, setShowEndByDatepicker] = useState(
    finalMappedValues.duration?.value !== 2 && finalMappedValues.endCriteria?.value !== 2
  );
  const [endBydateValue, setEndBydateValue] = useState(
    finalMappedValues.endBydateValue
      ? new Date(finalMappedValues.endBydateValue)
      : finalMappedValues.endDate
      ? new Date(finalMappedValues.endDate)
      : null
  );
  const [endByCountValue, setEndByCountValue] = useState(() => {
    if (finalMappedValues.endByCountValue !== undefined && finalMappedValues.endByCountValue !== null) {
      return finalMappedValues.endByCountValue;
    }

    if (initialValues.endByCountValue !== undefined && initialValues.endByCountValue !== null) {
      return initialValues.endByCountValue;
    }

    if (initialValues.recurrenceContext?.endByCountValue !== undefined && initialValues.recurrenceContext?.endByCountValue !== null) {
      return initialValues.recurrenceContext.endByCountValue;
    }

    if (initialValues.EndByCountValue !== undefined && initialValues.EndByCountValue !== null) {
      return initialValues.EndByCountValue;
    }
    return number.SEVEN;
  });
  const [skipCountValue, setSkipCountValue] = useState(finalMappedValues.frequencyDay || initialValues.skipCount || number.THREE);
  const [recurrenceMode, setRecurrenceMode] = useState(initialValues.recurrenceMode || number.ONE);
  const [skipPastDueDates, setSkipPastDueDates] = useState(initialValues.skipPastDueDates !== false);
  const [recurrDisabled, setRecurrDisabled] = useState(initialValues.recurrDisabled || false);
  const [edit, setEdit] = useState(initialValues.edit || false);
  const [project, setProject] = useState(initialValues.project);
  const [futureRecurrenceProject, setFutureRecurrenceProject] = useState(initialValues.futureRecurrenceProject);
  const [isSaveDisabled, setIsSaveDisabled] = useState(initialValues.isSaveDisabled || false);
  const [recurrenceCreator, setRecurrenceCreator] = useState(initialValues.recurrenceCreator);
  const [patternBasedRecur, setPatternBasedRecur] = useState(initialValues.patternBasedRecur);
  const [updatedPayload, setUpdatedPayload] = useState(initialValues.updatedPayload);
  const [assignedUser, setAssignedUser] = useState(initialValues.assignedUser);
  const [skipDays, setSkipDays] = useState([true]);
  const [assignedId, setAssignedId] = useState(initialValues.assignedId);
  const [assigneeLists, setAssigneeLists] = useState(initialValues.assigneeLists || "");
  const [projectUserData, setProjectUserData] = useState(initialValues.projectUserData);
  const [workflows, setWorkflows] = useState(initialValues.workflows || []);
  const [taskAssignmentType, setTaskAssignmentType] = useState(initialValues.taskAssignmentType);
  const [reminderValue, setReminderValue] = useState(initialValues.reminderValue);
  const [skipPastDate, setSkipPastDate] = useState(initialValues.skipPastDate || false);
  const [skipSaturday, setSkipSaturday] = useState(initialValues.skipSaturday || false);
  const [skipSunday, setSkipSunday] = useState(initialValues.skipSunday || false);
  const [frequencyInterval, setFrequencyInterval] = useState(number.ONE);
  const [frequencyIntervalType, setFrequencyIntervalType] = useState(null);
  const [weeklyCustomRecurDay, setWeeklyCustomRecurDay] = useState([]);
  const [nextRecurrenceDate, setNextRecurrenceDate] = useState(initialValues.nextRecurrenceDate ? new Date(initialValues.nextRecurrenceDate) : null);
  const [monthlyCustomRecurDates, setMonthlyCustomRecurDates] = useState([]);
  const [recurrenceEnabled, setRecurrenceEnabledState] = useState(finalMappedValues.recurrenceEnabled === true);

  const setRecurrenceEnabled = (value) => {
    setRecurrenceEnabledState(value);
    if (onRecurrenceEnabledChange) {
      onRecurrenceEnabledChange(value);
    }
  };

  const value = {
    calenderValue,
    setCalenderValue,
    recurrencePattern,
    setRecurrencePattern,
    recur,
    setRecur,
    reminder,
    setReminder,
    title,
    setTitle,
    duration,
    setDuration,
    showEndByDatepicker,
    setShowEndByDatepicker,
    endBydateValue,
    setEndBydateValue,
    endByCountValue,
    setEndByCountValue,
    skipCountValue,
    setSkipCountValue,
    recurrenceMode,
    setRecurrenceMode,
    skipPastDueDates,
    setSkipPastDueDates,
    recurrDisabled,
    setRecurrDisabled,
    edit,
    setEdit,
    project,
    setProject,
    isSaveDisabled,
    setIsSaveDisabled,
    recurrenceCreator,
    setRecurrenceCreator,
    patternBasedRecur,
    setPatternBasedRecur,
    updatedPayload,
    setUpdatedPayload,
    assignedUser,
    setAssignedUser,
    skipDays,
    setSkipDays,
    assignedId,
    setAssignedId,
    assigneeLists,
    setAssigneeLists,
    projectUserData,
    setProjectUserData,
    workflows,
    setWorkflows,
    taskAssignmentType,
    setTaskAssignmentType,
    reminderValue,
    setReminderValue,
    skipPastDate,
    setSkipPastDate,
    skipSaturday,
    setSkipSaturday,
    frequencyInterval,
    setFrequencyInterval,
    frequencyIntervalType,
    setFrequencyIntervalType,
    weeklyCustomRecurDay,
    setWeeklyCustomRecurDay,
    nextRecurrenceDate,
    setNextRecurrenceDate,
    monthlyCustomRecurDates,
    setMonthlyCustomRecurDates,
    skipSunday,
    setSkipSunday,
    futureRecurrenceProject,
    setFutureRecurrenceProject,
    recurrenceEnabled,
    setRecurrenceEnabled,
  };

  return <RecurringTaskContext.Provider value={value}>{children}</RecurringTaskContext.Provider>;
};
